import React, { useEffect, useState, useRef, useCallback } from "react";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import SelectedMonitorLog from "./components/SelectedMonitorLog";
import { getActiveFilters, parseLogData } from "./utils";
import { INITIAL_FILTERS } from "./constants";
import useMonitorLogs from "./hooks/useMonitorLogs";
import useOrgUsers from "./hooks/useOrgUsers";
import useOrgApps from "./hooks/useOrgApps";
import FilterSection from "./components/FilterSections";
import LogTable from "./components/LogTable";
import Loader from "../../Layouts/Loader/Loader";
import { getMonitorLogs } from "../../services/apis/monitorApis";
import { useParams } from "react-router-dom";
import BuilderRightSideBar from "../../Layouts/BuilderRightSideBar";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import Input from "../../components/FormComponents/Input";
import Button from "../../components/FormComponents/Button";

const MonitorLogs = () => {
  const limit = 100;
  const [pagination, setPagination] = useState({
    search: undefined,
    page: 1,
    limit: Number(limit)
  });
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  const { orgId } = useParams();

  const { logs, pageTrack, fetchLogs, setLogs, setPageTrack, logTypeOptions, getLogById } = useMonitorLogs(orgId);
  const { userOptions, fetchUsers: fetchOrgUsers } = useOrgUsers(orgId);
  const { appOptions, isLoading: isAppsLoading, fetchApps: fetchOrgApps } = useOrgApps(orgId);

  const debouncedSearchRef = useRef(null);
  const debouncedFilterRef = useRef(null);

  const metaData = parseLogData(selectedLog?.log_data);

  const refreshLogs = useCallback(async () => {
    const activeFilters = getActiveFilters(filters);
    await fetchLogs(pagination.search, pagination.page, pagination.limit, activeFilters);
  }, [filters, pagination, fetchLogs]);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setPagination((prev) => ({ ...prev, search: query, page: 1 }));
    debouncedSearchRef.current(query);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev, [filterName]: value };
      debouncedFilterRef.current(updatedFilters);
      return updatedFilters;
    });
  };

  const resetFilters = async () => {
    setFilters(INITIAL_FILTERS);
    await fetchLogs(pagination.search, 1, pagination.limit, {});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleRefresh = async () => {
    try {
      debouncedSearchRef.current?.cancel();
      debouncedFilterRef.current?.cancel();
      await refreshLogs();
      toast.success("Logs refreshed successfully");
    } catch (error) {
      console.error("Refresh error:", error);
    }
  };

  const onPageChangeAPI = async (pageNumber) => {
    try {
      const activeFilters = getActiveFilters(filters);
      await fetchLogs(pagination.search, pageNumber, pagination.limit, activeFilters);
      setPagination((prev) => ({ ...prev, page: pageNumber }));
    } catch (error) {
      console.error("Failed to fetch monitor logs:", error);
    }
  };

  const onLimitChangeAPI = async (newLimit) => {
    try {
      localStorage.setItem("limit", newLimit);
      const activeFilters = getActiveFilters(filters);
      await fetchLogs(pagination.search, pagination.page, newLimit, activeFilters);
      setPagination((prev) => ({ ...prev, limit: newLimit }));
    } catch (error) {
      console.error("Failed to fetch monitor logs:", error);
    }
  };

  const handleViewDetails = useCallback(
    async (summaryLog) => {
      setIsOpenDetails(true);
      setIsDetailsLoading(true);
      setSelectedLog(null);

      const fullLogData = await getLogById(summaryLog.id);

      if (fullLogData) {
        setSelectedLog(fullLogData);
      }
      setIsDetailsLoading(false);
    },
    [getLogById]
  );

  useEffect(() => {
    debouncedSearchRef.current = debounce(async (query) => {
      try {
        const activeFilters = getActiveFilters(filters);

        const response = await getMonitorLogs(orgId, {
          search: query?.trim() || undefined,
          page: 1,
          limit: pagination.limit,
          ...activeFilters
        });

        const data = response?.data;

        setLogs(data?.logs || []);
        setPageTrack({
          currPage: data?.page,
          totalPages: data?.total_pages
        });
        setPagination((prev) => ({ ...prev, page: 1 }));
      } catch (error) {
        console.error("Search error:", error);
      }
    }, 500);

    return () => debouncedSearchRef.current?.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.limit, orgId]);

  useEffect(() => {
    debouncedFilterRef.current = debounce(async (filtersToApply) => {
      try {
        const activeFilters = getActiveFilters(filtersToApply);

        const response = await getMonitorLogs(orgId, {
          search: pagination.search || undefined,
          page: 1,
          limit: pagination.limit,
          ...activeFilters
        });

        const data = response?.data;

        setLogs(data?.logs || []);
        setPageTrack({
          currPage: data?.page,
          totalPages: data?.total_pages
        });
        setPagination((prev) => ({ ...prev, page: 1 }));
      } catch (error) {
        console.error("Filter error:", error);
      }
    }, 800);

    return () => debouncedFilterRef.current?.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.search, pagination.limit, orgId]);

  useEffect(() => {
    fetchLogs(undefined, 1, Number(limit));
    fetchOrgUsers();
    fetchOrgApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="application_container">
      <AppLeftSideBar type="app" />
      <div className="h_100 d_flex flex_column gap_2 w_100 p_3">
        <div className="w_100 h_100 d_flex justify_between">
          <h2>Monitor Logs</h2>
          <div className="d_flex gap_2">
            <Input
              autoFocus
              placeholder="Search logs..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              className="w_100"
            />
            <div className="w_25">
              <Button variant="primary" onClick={handleRefresh} title="Refresh logs">
                Refresh
              </Button>
            </div>
          </div>
        </div>
        <FilterSection
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={resetFilters}
          userOptions={userOptions}
          appOptions={appOptions}
          logTypeOptions={logTypeOptions}
          isAppsLoading={isAppsLoading}
        />

        <LogTable
          logs={logs}
          onViewDetails={handleViewDetails}
          pageTrack={pageTrack}
          pagination={pagination}
          onPageChange={onPageChangeAPI}
          onLimitChange={onLimitChangeAPI}
        />
      </div>
      <BuilderRightSideBar
        isOpen={isOpenDetails}
        onClose={setIsOpenDetails}
        heading="Monitor Log Details"
        className="w_100 h_100"
      >
        <div
          style={{
            position: "relative",
            backgroundColor: "#f9fafb",
            height: "100%",
            padding: "12px"
          }}
        >
          <div
            style={{
              height: "100%",
              overflow: "auto"
            }}
          >
            {isDetailsLoading && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "40px",
                  alignItems: "center",
                  height: "100%"
                }}
              >
                <Loader />
              </div>
            )}

            {!isDetailsLoading && selectedLog && <SelectedMonitorLog selectedLog={selectedLog} metaData={metaData} />}
          </div>
        </div>
      </BuilderRightSideBar>
    </div>
  );
};

export default MonitorLogs;
